import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface SegmentLevelFilterProps {
    selectedLevel: number | null;
    onLevelChange: (level: number | null) => void;
}

function SegmentLevelFilter({ selectedLevel, onLevelChange }: SegmentLevelFilterProps) {
    const theme = useTheme();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang);
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);

    const getLevelColor = (levelIndex: number) => {
        return theme.palette.levels?.[levelIndex as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    return (
        <Box
            sx={{
                display: 'flex',
                gap: 1,
                alignItems: 'center',
                border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
            }}
        >
            <Box
                onClick={() => onLevelChange(null)}
                sx={{
                    minWidth: 32,
                    height: 24,
                    borderRadius: 1,
                    backgroundColor: selectedLevel === null ? theme.palette.primary.main : theme.palette.grey[300],
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    opacity: selectedLevel === null ? 1 : 0.3,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 0.5,
                    '&:hover': {
                        transform: 'scale(1.05)',
                        opacity: 1,
                    },
                }}
                title={langStrings.profile.all}
            >
                <Typography
                    variant="caption"
                    sx={{
                        fontSize: '0.65rem',
                        fontWeight: 500,
                        color: selectedLevel === null ? 'white' : theme.palette.text.secondary,
                    }}
                >
                    {langStrings.profile.all}
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                {Array.from({ length: levelDefinitions.length }, (_, i) => i + 1).map((level) => (
                    <Box
                        key={level}
                        onClick={() => onLevelChange(selectedLevel === level ? null : level)}
                        sx={{
                            width: 12,
                            height: 24,
                            borderRadius: 1,
                            backgroundColor:
                                selectedLevel === null || level <= (selectedLevel || 0)
                                    ? getLevelColor(level)
                                    : theme.palette.grey[300],
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            opacity: selectedLevel === null || level <= (selectedLevel || 0) ? 1 : 0.3,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                opacity: 1,
                            },
                        }}
                        title={`${levelDefinitions[level - 1]?.title || `Level ${level}`}`}
                    />
                ))}
            </Box>
        </Box>
    );
}

export default SegmentLevelFilter;
